export default {
  id: 'point-of-sale',
  title: 'Prodajna mjesta',
  icon: 'mdi-store',
  show: true,
  order: 3,
  children: [
    {
      id: 'pos-what-is',
      title: 'Što je prodajno mjesto?',
      icon: 'mdi-information',
      show: true,
      order: 1,
      content: () => `
      <h2 id="what-is-pos">Što je prodajno mjesto?</h2>
      <p>
        Prodajno mjesto (Point of Sale - POS) predstavlja fizičku lokaciju gdje se odvija prodaja robe ili usluga.
        U AI Kasa sustavu, jedno poduzeće može imati jedno ili više prodajnih mjesta,
        ovisno o broju poslovnica, trgovina, restorana ili drugih lokacija.
      </p>

      <h3 class="mt-4">Glavne karakteristike prodajnog mjesta</h3>

      <table class="info-table">
        <thead>
          <tr>
            <th>Karakteristika</th>
            <th>Opis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Jedinstvena lokacija</strong></td>
            <td>Svako prodajno mjesto ima vlastitu adresu i kontakt podatke</td>
          </tr>
          <tr>
            <td><strong>Tip lokacije</strong></td>
            <td>Trgovina, Restoran, Ugostiteljstvo, Servis, ili custom tip</td>
          </tr>
          <tr>
            <td><strong>Vlastite blagajne</strong></td>
            <td>Svako prodajno mjesto ima jednu ili više blagajna</td>
          </tr>
          <tr>
            <td><strong>Specifičan cjenik</strong></td>
            <td>Mogućnost definiranja različitih cijena po lokaciji</td>
          </tr>
          <tr>
            <td><strong>Radno vrijeme</strong></td>
            <td>Definirano radno vrijeme za svaki dan u tjednu</td>
          </tr>
          <tr>
            <td><strong>Zaseban izvještaji</strong></td>
            <td>Izvještaji i statistika po prodajnom mjestu</td>
          </tr>
        </tbody>
      </table>

      <h3 class="mt-4">Primjeri korištenja</h3>

      <h4 class="mt-3">Primjer 1: Maloprodajni lanac</h4>
      <p>
        Poduzeće "Trgovina ABC" ima tri prodajna mjesta:
      </p>
      <ul>
        <li><strong>Trgovina ABC Centar</strong> - glavna poslovnica u centru grada s 3 blagajne</li>
        <li><strong>Trgovina ABC Istok</strong> - poslovnica na istoku grada s 2 blagajne</li>
        <li><strong>Trgovina ABC Zapad</strong> - poslovnica na zapadu grada s 1 blagajnom</li>
      </ul>

      <h4 class="mt-3">Primjer 2: Restoranski lanac</h4>
      <p>
        Poduzeće "Restoran Dalmacija" ima dva prodajna mjesta:
      </p>
      <ul>
        <li><strong>Restoran Dalmacija Split</strong> - glavni restoran sa stolovnim narudžbama</li>
        <li><strong>Restoran Dalmacija Trogir</strong> - obiteljski restoran sa terasom</li>
      </ul>

      <h3 class="mt-4">Organizacijska hijerarhija</h3>

      <div class="info-box">
        <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Hijerarhija sustava:</strong><br>
          <strong>Firma</strong> (poduzeće/tvrtka)<br>
          └── <strong>Prodajno mjesto</strong> (fizička lokacija)<br>
          &nbsp;&nbsp;&nbsp;&nbsp;└── <strong>Blagajna</strong> (uređaj za naplatu)<br>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;└── <strong>Korisnik</strong> (blagajnik)
        </div>
      </div>

      <h3 class="mt-4">Prednosti višestrukih prodajnih mjesta</h3>

      <ul>
        <li><strong>Centralizirana administracija</strong> - sve lokacije u jednom sustavu</li>
        <li><strong>Zasebni izvještaji</strong> - analiza po pojedinačnoj lokaciji</li>
        <li><strong>Različiti cjenici</strong> - fleksibilno određivanje cijena po lokaciji</li>
        <li><strong>Kontrola zaliha</strong> - praćenje stanja skladišta po prodajnom mjestu</li>
        <li><strong>Upravljanje zaposlenicima</strong> - dodjeljivanje korisnika određenim lokacijama</li>
      </ul>

      <div class="success-box">
        <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Savjet:</strong> Ako imate samo jednu lokaciju, kreirajte jedno prodajno mjesto.
          Sustav je dizajniran da podržava i male trgovine s jednom blagajnom kao i velike lance s više lokacija.
        </div>
      </div>

      <h3 class="mt-4">Često postavljana pitanja</h3>

      <h4 class="mt-3">Koliko prodajnih mjesta mogu imati?</h4>
      <p>
        Nema ograničenja broja prodajnih mjesta. Možete imati neograničen broj lokacija,
        ovisno o vašem pretplatničkom paketu.
      </p>

      <h4 class="mt-3">Mogu li imati različite cijene po prodajnom mjestu?</h4>
      <p>
        Da, svako prodajno mjesto može imati vlastiti cjenik s različitim cijenama.
        Na primjer, jedna lokacija može imati više cijene od druge.
      </p>

      <h4 class="mt-3">Što ako zatvorim jedno prodajno mjesto?</h4>
      <p>
        Možete deaktivirati prodajno mjesto, ali svi povijesni podaci i računi ostaju
        dostupni za izvještaje i evidenciju.
      </p>
    `
    },
    {
      id: 'pos-add',
      title: 'Dodavanje prodajnog mjesta',
      icon: 'mdi-plus',
      show: true,
      order: 2,
      content: () => `
      <h2 id="add-pos">Dodavanje prodajnog mjesta</h2>
      <p>
        Prodajno mjesto (Point of Sale - POS) predstavlja fizičku lokaciju gdje se odvija prodaja.
        Jedna firma može imati više prodajnih mjesta (npr. više trgovina, restorana).
      </p>

      <h3 class="mt-4">Korak po korak</h3>

      <h4 class="mt-3">1. Otvaranje forme</h4>
      <ol>
        <li>Idite na <strong>"Postavke" → "Prodajna mjesta"</strong></li>
        <li>Kliknite <strong>"+ Dodaj prodajno mjesto"</strong></li>
      </ol>

      <h4 class="mt-3">2. Osnovni podaci</h4>
      <ul>
        <li><strong>Naziv *</strong> - Naziv prodajnog mjesta (npr. "Trgovina Centar", "Restoran Splitska")</li>
        <li><strong>Kratica</strong> - Skraćeni naziv (npr. "TC", "RSP")</li>
        <li><strong>Tip</strong> - Odaberite tip: Trgovina, Restoran, Ugostiteljstvo, Servis</li>
      </ul>

      <h4 class="mt-3">3. Adresa</h4>
      <ul>
        <li><strong>Ulica i broj *</strong></li>
        <li><strong>Poštanski broj *</strong></li>
        <li><strong>Grad *</strong></li>
        <li><strong>Država *</strong></li>
      </ul>

      <h4 class="mt-3">4. Kontakt informacije</h4>
      <ul>
        <li><strong>Telefon</strong></li>
        <li><strong>Email</strong></li>
        <li><strong>Web stranica</strong></li>
      </ul>

      <h4 class="mt-3">5. Radno vrijeme (opcionalno)</h4>
      <p>Definirajte radno vrijeme za svaki dan:</p>
      <ul>
        <li>Odaberite dane u tjednu</li>
        <li>Unesite vrijeme otvaranja i zatvaranja</li>
        <li>Možete dodati pauzu (npr. 12:00-13:00)</li>
      </ul>

      <h4 class="mt-3">6. Spremite</h4>
      <p>Kliknite <strong>"Spremi"</strong> da kreirate prodajno mjesto.</p>

      <div class="success-box">
        <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Savjet:</strong> Nakon kreiranja prodajnog mjesta, dodajte blagajne i postavite cjenik specifičan za to mjesto.
        </div>
      </div>
    `
    },
    {
      id: 'pos-settings',
      title: 'Postavke prodajnog mjesta',
      icon: 'mdi-cog',
      show: true,
      order: 3,
      content: () => `
      <h2 id="pos-settings">Postavke prodajnog mjesta</h2>
      <p>
        Nakon kreiranja prodajnog mjesta, možete urediti sve njegove postavke:
        osnovne informacije, adresu, kontakt podatke, radno vrijeme i druge specifične opcije.
      </p>

      <h3 class="mt-4">Pristup postavkama</h3>

      <h4 class="mt-3">Način 1: Iz liste prodajnih mjesta</h4>
      <ol>
        <li>Idite na <strong>"Postavke" → "Prodajna mjesta"</strong></li>
        <li>Pronađite prodajno mjesto u listi</li>
        <li>Kliknite <i class="mdi mdi-cog"></i> <strong>"Postavke"</strong> ili <i class="mdi mdi-pencil"></i> <strong>"Uredi"</strong></li>
      </ol>

      <h4 class="mt-3">Način 2: Iz pregleda prodajnog mjesta</h4>
      <ol>
        <li>Kliknite na naziv prodajnog mjesta u listi</li>
        <li>Otvorit će se detalji prodajnog mjesta</li>
        <li>Kliknite <strong>"Uredi postavke"</strong></li>
      </ol>

      <h3 class="mt-4">Osnovne informacije</h3>

      <table class="info-table">
        <thead>
          <tr>
            <th>Polje</th>
            <th>Opis</th>
            <th>Obavezno</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Naziv</strong></td>
            <td>Puni naziv prodajnog mjesta (npr. "Trgovina ABC Centar")</td>
            <td><span class="status-chip error">Da</span></td>
          </tr>
          <tr>
            <td><strong>Kratica</strong></td>
            <td>Skraćeni naziv (npr. "TAC", max 10 znakova)</td>
            <td><span class="status-chip">Ne</span></td>
          </tr>
          <tr>
            <td><strong>Tip</strong></td>
            <td>Trgovina, Restoran, Ugostiteljstvo, Servis, Ostalo</td>
            <td><span class="status-chip error">Da</span></td>
          </tr>
          <tr>
            <td><strong>Status</strong></td>
            <td>Aktivno / Neaktivno</td>
            <td><span class="status-chip error">Da</span></td>
          </tr>
        </tbody>
      </table>

      <div class="warning-box">
        <i class="mdi mdi-alert" style="color: #FF9800; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Napomena:</strong> Ako deaktivirate prodajno mjesto, ono neće biti dostupno za rad,
          ali svi povijesni podaci ostaju sačuvani.
        </div>
      </div>

      <h3 class="mt-4">Adresa</h3>

      <p>Unesite točnu adresu prodajnog mjesta:</p>
      <ul>
        <li><strong>Ulica i broj *</strong> - Naziv ulice i kućni broj</li>
        <li><strong>Poštanski broj *</strong> - 5-znamenkasti broj</li>
        <li><strong>Grad *</strong> - Naziv grada ili mjesta</li>
        <li><strong>Država *</strong> - Odaberite državu iz popisa</li>
      </ul>

      <div class="info-box">
        <i class="mdi mdi-information" style="color: #3989C9; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Važno:</strong> Adresa se prikazuje na fiskalnim računima i R1/R2 računima,
          te mora biti ispravna i potpuna.
        </div>
      </div>

      <h3 class="mt-4">Kontakt informacije</h3>

      <table class="info-table">
        <thead>
          <tr>
            <th>Polje</th>
            <th>Format</th>
            <th>Primjer</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Telefon</strong></td>
            <td>Međunarodni format</td>
            <td>+385 21 123 456</td>
          </tr>
          <tr>
            <td><strong>Email</strong></td>
            <td>Valjana email adresa</td>
            <td>centar@trgovina.hr</td>
          </tr>
          <tr>
            <td><strong>Web stranica</strong></td>
            <td>URL sa http/https</td>
            <td>https://www.trgovina.hr</td>
          </tr>
          <tr>
            <td><strong>Fax</strong></td>
            <td>Broj faksa</td>
            <td>+385 21 123 457</td>
          </tr>
        </tbody>
      </table>

      <h3 class="mt-4">Radno vrijeme</h3>

      <p>Definirajte radno vrijeme prodajnog mjesta za svaki dan u tjednu:</p>

      <h4 class="mt-3">Postavljanje radnog vremena</h4>
      <ol>
        <li>Odaberite dan u tjednu (Ponedjeljak - Nedjelja)</li>
        <li>Označite "Radi" ako je prodajno mjesto otvoreno taj dan</li>
        <li>Unesite <strong>vrijeme otvaranja</strong> (npr. 08:00)</li>
        <li>Unesite <strong>vrijeme zatvaranja</strong> (npr. 20:00)</li>
        <li>Opcionalno: Dodajte <strong>pauzu</strong> (npr. 12:00 - 13:00)</li>
        <li>Kliknite <strong>"Dodaj"</strong> za dodavanje radnog vremena</li>
      </ol>

      <div class="success-box">
        <i class="mdi mdi-lightbulb" style="color: #4CAF50; margin-right: 8px; font-size: 20px;"></i>
        <div>
          <strong>Savjet:</strong> Možete kopirati radno vrijeme na druge dane klikom na "Kopiraj na ostale dane".
          To je korisno ako imate isto radno vrijeme radnim danima.
        </div>
      </div>

      <h4 class="mt-3">Primjer: Radno vrijeme trgovine</h4>
      <table class="info-table">
        <thead>
          <tr>
            <th>Dan</th>
            <th>Otvaranje</th>
            <th>Zatvaranje</th>
            <th>Pauza</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Ponedjeljak - Petak</td>
            <td>08:00</td>
            <td>20:00</td>
            <td>-</td>
          </tr>
          <tr>
            <td>Subota</td>
            <td>08:00</td>
            <td>14:00</td>
            <td>-</td>
          </tr>
          <tr>
            <td>Nedjelja</td>
            <td colspan="3" style="text-align: center;">Zatvoreno</td>
          </tr>
        </tbody>
      </table>

      <h3 class="mt-4">Dodatne postavke</h3>

      <ul>
        <li><strong>Logo</strong> - Učitajte logo prodajnog mjesta (prikazuje se na računima)</li>
        <li><strong>Boja teme</strong> - Odaberite boju za vizualnu identifikaciju</li>
        <li><strong>Vrsta djelatnosti</strong> - Šifra prema NKD-u (Nacionalnoj klasifikaciji djelatnosti)</li>
        <li><strong>Napomena</strong> - Dodatni tekst koji se može prikazati na računima</li>
      </ul>

      <h3 class="mt-4">Često postavljana pitanja</h3>

      <h4 class="mt-3">Mogu li promijeniti adresu prodajnog mjesta?</h4>
      <p>
        Da, možete promijeniti adresu u bilo kojem trenutku. Nova adresa će se
        prikazivati na računima nakon spremanja izmjena.
      </p>

      <h4 class="mt-3">Što ako ne unesem radno vrijeme?</h4>
      <p>
        Radno vrijeme je opcionalno. Ako ga ne definirate, neće se prikazivati
        na računima niti u sustavu.
      </p>

      <h4 class="mt-3">Može li jedno prodajno mjesto imati više adresa?</h4>
      <p>
        Ne, jedno prodajno mjesto ima jednu adresu. Ako imate više fizičkih lokacija,
        kreirajte zasebno prodajno mjesto za svaku.
      </p>
    `
    }
  ]
}
